import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Link } from "react-router-dom";
import { Star, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { FontFamily } from "@/lib/api";
import FontFaces, { fontStack } from "@/components/FontFaces";

const CATEGORY_RANK: Record<string, number> = { hand: 0, serif: 1, sans: 2, display: 3 };

function sortFonts(rows: FontFamily[]) {
  return [...rows].sort(
    (a, b) =>
      Number(Boolean(b.starred)) - Number(Boolean(a.starred)) ||
      Number(Boolean(b.available)) - Number(Boolean(a.available)) ||
      (CATEGORY_RANK[a.category || ""] ?? 9) - (CATEGORY_RANK[b.category || ""] ?? 9) ||
      a.name.localeCompare(b.name, "pl"),
  );
}

export default function FontPicker({
  fonts,
  value,
  onChange,
  starredOnly = false,
}: {
  fonts: FontFamily[];
  value?: string;
  onChange: (id: string) => void;
  /** W składzie karuzel: lista = gwiazdki (+ bieżący wybór). */
  starredOnly?: boolean;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [baseline, setBaseline] = useState<string | undefined>();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const current =
    fonts.find((row) => row.id === value) || fonts.find((row) => row.id === "source-serif");
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let rows = fonts;
    if (starredOnly && !showAll && !needle) {
      const starred = fonts.filter((row) => row.starred);
      const base = starred.length
        ? [...starred]
        : fonts.filter((row) => row.id === "source-serif" || row.id === "source-sans");
      const keep = new Set(base.map((row) => row.id));
      if (value && !keep.has(value)) {
        const extra = fonts.find((row) => row.id === value);
        if (extra) base.push(extra);
      }
      rows = base;
    }
    const filtered = rows.filter(
      (row) => !needle || row.name.toLowerCase().includes(needle) || row.id.includes(needle),
    );
    return sortFonts(filtered);
  }, [fonts, q, starredOnly, showAll, value]);
  const faces = current ? [current, ...shown.filter((row) => row.id !== current.id)] : shown;
  const starredCount = fonts.filter((row) => row.starred).length;

  const preview = (id: string) => {
    if (id && id !== value) onChange(id);
  };

  const closeClean = () => {
    setOpen(false);
    setQ("");
    setShowAll(false);
    queueMicrotask(() => triggerRef.current?.focus());
  };

  const cancel = () => {
    if (baseline !== undefined && baseline !== value) onChange(baseline);
    closeClean();
  };

  const apply = () => {
    closeClean();
  };

  const openPicker = () => {
    setBaseline(value || current?.id);
    setShowAll(false);
    setQ("");
    setOpen(true);
  };

  const moveActive = (delta: number) => {
    if (!shown.length) return;
    const idx = shown.findIndex((row) => row.id === (value || current?.id));
    const from = idx < 0 ? (delta > 0 ? -1 : 0) : idx;
    const next = Math.max(0, Math.min(shown.length - 1, from + delta));
    const row = shown[next];
    if (row) preview(row.id);
  };

  useEffect(() => {
    if (!open) return;
    const active = listRef.current?.querySelector<HTMLElement>('[data-font-active="1"]');
    active?.scrollIntoView({ block: "nearest" });
  }, [open, value, shown]);

  const onPanelKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveActive(1);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      moveActive(-1);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      apply();
    }
  };

  return (
    <div className="relative min-w-[12rem]">
      <FontFaces fonts={faces} />
      <button
        ref={triggerRef}
        type="button"
        className="flex h-8 w-full items-center justify-between rounded-md border border-input bg-transparent px-2 text-xs"
        style={{ fontFamily: fontStack(current) }}
        onClick={() => {
          if (!open) openPicker();
        }}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span className="truncate">{current?.name || "Font"}</span>
        <span className="ml-2 text-[10px] text-muted-foreground">{current?.starred ? "★" : ""}</span>
      </button>
      {open ? (
        <div
          className="absolute z-30 mt-1 w-72 rounded-md border border-border bg-card p-2 shadow-lg"
          role="dialog"
          aria-label="Wybór fontu"
          onKeyDown={onPanelKeyDown}
        >
          <div className="mb-2 flex items-center gap-2">
            <input
              className="h-8 min-w-0 flex-1 rounded-md border border-input bg-transparent px-2 text-xs"
              placeholder={starredOnly && !showAll ? "szukaj w gwiazdkach…" : "szukaj fontu…"}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
              aria-label="Szukaj fontu"
            />
            <button
              type="button"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-input text-muted-foreground hover:bg-accent/50"
              title="Anuluj i przywróć poprzedni font"
              aria-label="Zamknij bez zapisu"
              onClick={cancel}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {starredOnly ? (
            <div className="mb-2 flex flex-wrap items-center gap-2 px-1">
              <button
                type="button"
                className={cn(
                  "rounded px-1.5 py-0.5 text-[10px]",
                  !showAll ? "bg-brand/15 text-brand" : "text-muted-foreground hover:bg-accent/50",
                )}
                onClick={() => setShowAll(false)}
              >
                Gwiazdki ({starredCount})
              </button>
              <button
                type="button"
                className={cn(
                  "rounded px-1.5 py-0.5 text-[10px]",
                  showAll ? "bg-brand/15 text-brand" : "text-muted-foreground hover:bg-accent/50",
                )}
                onClick={() => setShowAll(true)}
              >
                Wszystkie
              </button>
            </div>
          ) : null}
          <div ref={listRef} className="max-h-64 overflow-auto" role="listbox">
            {shown.map((row) => {
              const active = row.id === current?.id;
              return (
                <button
                  key={row.id}
                  type="button"
                  role="option"
                  aria-selected={active}
                  data-font-active={active ? "1" : undefined}
                  className={cn(
                    "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs",
                    active ? "bg-brand/10" : "hover:bg-accent/50",
                    !row.available ? "opacity-60" : "",
                  )}
                  style={{ fontFamily: fontStack(row) }}
                  onClick={() => preview(row.id)}
                  onDoubleClick={() => {
                    preview(row.id);
                    apply();
                  }}
                >
                  <Star
                    className={cn(
                      "h-3 w-3 shrink-0",
                      row.starred ? "fill-current text-brand" : "text-muted-foreground",
                    )}
                  />
                  <span className="min-w-0 flex-1 truncate">{row.name}</span>
                  <span className="shrink-0 text-[10px] text-muted-foreground">
                    {!row.available
                      ? "brak pliku"
                      : row.category === "hand"
                        ? "pisane"
                        : row.category === "display"
                          ? "ozdobne"
                          : ""}
                  </span>
                </button>
              );
            })}
            {!shown.length ? (
              <p className="px-2 py-3 text-[11px] text-muted-foreground">
                {q.trim()
                  ? "Brak fontów dla tego wyszukiwania."
                  : starredOnly && !showAll
                    ? "Brak gwiazdek. Oznacz fonty na stronie Fonty."
                    : "Brak fontów na liście."}
              </p>
            ) : null}
          </div>
          <div className="mt-2 flex items-center justify-between gap-2 border-t border-border pt-2">
            <Link
              to="/fonty"
              className="px-1 text-[11px] text-brand underline"
              onClick={() => cancel()}
            >
              Fonty (gwiazdki)
            </Link>
            <button
              type="button"
              className="h-8 rounded-md bg-brand px-3 text-xs font-medium text-brand-foreground hover:opacity-90"
              onClick={apply}
            >
              Zastosuj
            </button>
          </div>
          <p className="mt-1 px-1 text-[10px] text-muted-foreground">
            ↑↓ podgląd na canvas · Esc / ✕ przywraca · Zastosuj zostawia
          </p>
        </div>
      ) : null}
    </div>
  );
}
